import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  GradeBadgeThermometerSummary,
  GradeBadgeThermometerSummaryLoadingView
} from './grade-badge-thermometer-summary'
import {
  VrModuleTitle,
  VrModulePanel,
  VrModuleFlexDirection,
  VrModuleTitleLoadingView
} from '../layout'
import { GradeBadge } from '../../../grades'
import { WithTooltip } from '../../../../design-system'
import {
  LoadingMaskRectangle,
  LoadingMaskGradeBadgeLarge
} from '../../../../legacy-design-system/loading-masks'
import { GradeValue } from '../../../../graphql/schema/types/grade-value'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import { theme as deprecatedTheme } from '../../../../styled'

export interface VrModuleOverallApplicationRatingData {
  readonly overallScore: number
  readonly overallGrade: GradeValue
  readonly appCount: number
  readonly viewData: Array<{
    readonly value: GradeValue
    readonly count: number
  }>
}
export interface VrFacetProps {
  readonly organizationUuid: string
  readonly selectedFacets: SelectedValueReportingFacet[]
}
export interface VrModuleOverallApplicationRatingProps {
  readonly isDataLoading: boolean
  readonly data?: VrModuleOverallApplicationRatingData
}

const cssScoreContent = css`
  margin-right: 20px;
`
const cssBreakdownContent = css`
  width: 100%;
`
const cssApplicationCount = css`
  margin-left: 0.5em;
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  font-size: 12px;
  color: ${theme.color.neutral60};
`
const cssOverallScoreCount = css`
  font-weight: ${deprecatedTheme.fontWeights.primary.bold};
  margin-left: 0.2em;
`
const cssOverallTooltip = css`
  display: flex;
  flex-direction: column;
  max-width: 300px;
`
const cssBreakdownSummaryHeader = css`
  margin-bottom: 10px;
`

const Wrapper = ({
  children,
  e2eTestId
}: {
  readonly children: React.ReactNode
  readonly e2eTestId?: string
}) => (
  <VrModulePanel hasBorder={false} order={1} minHeight={300} testId={e2eTestId}>
    <div
      className={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {children}
    </div>
  </VrModulePanel>
)

const InnerWrapper = ({
  flexDirection,
  children
}: {
  readonly flexDirection: VrModuleFlexDirection
  readonly children: React.ReactNode
}) => (
  <div
    className={css`
      display: flex;
      flex-direction: ${flexDirection};
      width: 100%;
    `}
  >
    {children}
  </div>
)

export function VrModuleOverallApplicationRating({
  isDataLoading,
  data,
  organizationUuid,
  selectedFacets
}: VrModuleOverallApplicationRatingProps & VrFacetProps) {
  const flexDirection: VrModuleFlexDirection = 'row'

  if (isDataLoading) {
    return (
      <Wrapper>
        <LoadingView flexDirection={flexDirection} />
      </Wrapper>
    )
  }

  if (!data) {
    throw new Error('data is undefined')
  }

  return (
    <Wrapper e2eTestId="value-reporting-overall-app-rating-module">
      <VrModuleTitle id="title_overallAppRating" />
      <InnerWrapper flexDirection={flexDirection}>
        <OverallApplicationRatingTotalScore {...data} />
        <OverallApplicationRatingBreakdown
          selectedFacets={selectedFacets}
          organizationUuid={organizationUuid}
          {...data}
        />
      </InnerWrapper>
    </Wrapper>
  )
}

function LoadingView({
  flexDirection
}: {
  readonly flexDirection: VrModuleFlexDirection
}) {
  return (
    <span data-testid="value-reporting-module-overall-app-rating-loading-view">
      <VrModuleTitleLoadingView />

      <InnerWrapper flexDirection={flexDirection}>
        <OverallApplicationRatingTotalScoreLoadingView />
        <OverallApplicationRatingBreakdownLoadingView />
      </InnerWrapper>
    </span>
  )
}

function OverallApplicationRatingTotalScoreLoadingView() {
  return (
    <section className={cssScoreContent}>
      <LoadingMaskGradeBadgeLarge />
      <div
        className={css`
          margin-top: 10px;
        `}
      >
        <LoadingMaskRectangle width={75} height={20} colorValue="lighter" />
      </div>
    </section>
  )
}

function OverallApplicationRatingTotalScore({
  overallScore,
  overallGrade
}: VrModuleOverallApplicationRatingData) {
  const intl = useIntl()

  return (
    <section className={cssScoreContent}>
      <WithTooltip
        placement="bottom-start"
        renderTooltipContent={() => (
          <div className={cssOverallTooltip}>
            <FormattedMessage id="detail_overallApplicationScore" />
            <FormattedMessage
              id="detail_overallApplicationLearnMoreLink"
              children={(msg) => (
                <a
                  href={intl.formatMessage({
                    id: 'link_applicationScoringGuide'
                  })}
                  target="_blank"
                >
                  {msg}
                </a>
              )}
            />
          </div>
        )}
        children={({ ref, onMouseEnter, onMouseLeave }) => (
          <span
            ref={ref}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <GradeBadge
              value={overallGrade}
              large={true}
              dataTestId="value-reporting-overall-app-rating-module-grade"
              e2eTestId="value-reporting-overall-app-rating-module-grade"
            />
          </span>
        )}
      />
      <div
        className={css`
          margin-top: 10px;
        `}
        data-testid="value-reporting-overall-app-rating-module-score"
        data-e2e="value-reporting-overall-app-rating-module-score"
      >
        <FormattedMessage id="title_score" />
        <span className={cssOverallScoreCount}>{overallScore}</span>
      </div>
    </section>
  )
}

function OverallApplicationRatingBreakdownLoadingView() {
  return (
    <section className={cssBreakdownContent}>
      <div className={cssBreakdownSummaryHeader}>
        <LoadingMaskRectangle width={100} height={19} colorValue="lighter" />
      </div>
      <GradeBadgeThermometerSummaryLoadingView />
    </section>
  )
}

function OverallApplicationRatingBreakdown({
  appCount,
  viewData,
  organizationUuid,
  selectedFacets
}: VrModuleOverallApplicationRatingData & VrFacetProps) {
  return (
    <section className={cssBreakdownContent}>
      <div
        className={cssBreakdownSummaryHeader}
        data-e2e="value-reporting-overall-app-rating-module-total"
      >
        <FormattedMessage id="title_allApplicationsLicensed" />
        <span className={cssApplicationCount}>({appCount})</span>
      </div>
      <GradeBadgeThermometerSummary
        selectedFacets={selectedFacets}
        organizationUuid={organizationUuid}
        data={viewData}
      />
    </section>
  )
}
